import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from '@mui/material';

interface ActionPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (remark?: string) => void;
  title: string;
  message?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  initialRemark?: string;
  textFieldLabel?: string;
  textFieldPlaceholder?: string;
  loading?: boolean;
  textFieldOptional?: boolean;
  required?: boolean;
}

const ActionPopup: React.FC<ActionPopupProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  message,
  submitButtonText = 'Submit',
  cancelButtonText = 'Cancel',
  initialRemark = '',
  textFieldLabel = 'Remark',
  textFieldPlaceholder = 'Enter your remark here...',
  loading = false,
  textFieldOptional = false,
  required = false,
}) => {
  const [remark, setRemark] = useState<string>(initialRemark);

  // Reset remark when the dialog opens or closes
  React.useEffect(() => {
    if (!open) {
      setRemark(initialRemark);
    }
  }, [open, initialRemark]);

  const handleSubmit = () => {
    onSubmit(remark);
    // Do not clear remark here, wait for success/close
  };

  const handleClose = () => {
    onClose();
    // Remark clearing is handled by useEffect when open becomes false
  };

  const isSubmitDisabled = loading || (required && !textFieldOptional && !remark.trim());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {message && <Typography variant="body2" sx={{ mb: 2 }}>{message}</Typography>}
        {
          !textFieldOptional && <TextField
            autoFocus
            margin="dense"
            id="remark"
            label={textFieldLabel}
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={textFieldPlaceholder}
            required={required}
            error={required && !remark.trim()}
          />
        }
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={handleClose} color="primary">
          {cancelButtonText}
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isSubmitDisabled}>
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionPopup;
